import { type AriaToastProps, useToast, useToastRegion } from "@react-aria/toast";
import { type ToastState, useToastState } from "@react-stately/toast";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useComponentsTranslation } from "../i18n/i18next.ts";
import { Toast } from "./Toast.tsx";
import { ToastRegion, type ToastRegionProps } from "./ToastRegion.tsx";
import { type ToastContent, ToastContext } from "./useToast.ts";

export interface StatefulToastProps extends AriaToastProps<ToastContent> {
    state: ToastState<ToastContent>;
    onAfterClose: () => void;
}

function StatefulToast({ state, onAfterClose, ...props }: StatefulToastProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [onClose, setOnClose] = useState<() => void>(() => {});
    // Flips to true once onClose below is wired to the real close handler. Tests can wait on
    // this attribute instead of racing a click against the effect that wires it up post-mount.
    const [isReady, setIsReady] = useState(false);

    const {
        toastProps,
        titleProps,
        closeButtonProps: { ...closeButtonProps }
    } = useToast(props, state, ref);

    useEffect(() => {
        // Animate in
        const button = ref.current;
        if (button) {
            button.animate(
                [
                    { opacity: 0, transform: "translateY(-100%)" },
                    { opacity: 1, transform: "translateY(0%)" }
                ],
                {
                    duration: 200,
                    easing: "ease-in-out",
                    fill: "forwards"
                }
            );
        }
    }, []);

    const toast = props.toast;

    useEffect(() => {
        setOnClose(() => () => {
            const button = ref.current;
            if (button) {
                const animation = button.animate(
                    [
                        { opacity: 1, transform: "scale(1)" },
                        { opacity: 0, transform: "scale(0.8)" }
                    ],
                    {
                        duration: 200,
                        easing: "ease-in-out",
                        fill: "forwards"
                    }
                );

                animation.onfinish = () => {
                    state.close(toast.key);
                    onAfterClose();
                };
            }
        });

        setIsReady(true);
    }, [state, toast.key, onAfterClose]);

    return (
        <Toast
            {...toastProps}
            data-ready={isReady}
            variant={toast.content.variant}
            message={toast.content.message}
            action={toast.content.action}
            durationInMs={toast.content.durationInMs}
            size={toast.content.size}
            onClose={onClose}
            titleProps={titleProps}
            closeButtonProps={closeButtonProps}
            ref={ref}
        />
    );
}

export interface ToastProviderProps extends Omit<ToastRegionProps, "aria-label"> {
    children: ReactNode;
}

export function ToastProvider({ children, ...rest }: ToastProviderProps) {
    const { t } = useComponentsTranslation();
    const ariaLabel = t("ToastProvider.label");

    const ref = useRef(null);

    const toastState = useToastState<ToastContent>({
        maxVisibleToasts: 1
    });

    const {
        regionProps: { "aria-label": regionPropsAriaLabel, ...regionProps }
    } = useToastRegion({ "aria-label": ariaLabel }, toastState, ref);

    // react-stately's `add` unshifts + immediately recomputes `visibleToasts` (capped to 1 here),
    // so calling it while a toast is already showing would silently preempt/unmount it. Queue
    // FIFO instead, and only advance to the next one once the current toast has fully closed.
    // A ref (not state) tracks "is a toast currently showing" so two toasts requested in the same
    // tick can't both read a stale "none visible" and both call add() directly.
    const hasVisibleToastRef = useRef(false);
    const pendingToastsRef = useRef<ToastContent[]>([]);

    function handleShowToast(content: ToastContent) {
        if (hasVisibleToastRef.current) {
            pendingToastsRef.current.push(content);
            return;
        }

        hasVisibleToastRef.current = true;

        // No `timeout` option here on purpose: react-stately's own auto-close timer would race
        // the progress-bar animation in Toast.tsx and can't be paused on hover/focus. The
        // animation is the single source of truth for auto-dismiss timing (see Toast.tsx).
        toastState.add(content);
    }

    function handleToastClosed() {
        const next = pendingToastsRef.current.shift();

        if (next) {
            toastState.add(next);
        } else {
            hasVisibleToastRef.current = false;
        }
    }

    return (
        <ToastContext.Provider value={handleShowToast}>
            {useMemo(() => children, [children])}
            {toastState.visibleToasts.length > 0 && (
                <ToastRegion {...rest} {...regionProps} aria-label={regionPropsAriaLabel!} ref={ref}>
                    {toastState.visibleToasts.map(toast => (
                        <StatefulToast
                            key={toast.key}
                            toast={toast}
                            state={toastState}
                            onAfterClose={handleToastClosed}
                        />
                    ))}
                </ToastRegion>
            )}
        </ToastContext.Provider>
    );
}
